import type { SetStateAction } from 'react';

import type { AgentChatUiMessage } from '~/features/studio/AgentChatThread';
import type { AgentReferenceImage } from '~/features/studio/AgentReferenceImageList';

import {
  type AgentComposerSessionSnapshot,
  DEFAULT_AGENT_COMPOSER_SESSION_TITLE,
} from './agentComposerSessionTypes';
import { readAgentComposerSessionSnapshot } from './useWorkspaceAgentComposerSession';
import { useWorkspaceStore } from './workspaceStore';

const resolveSetStateAction = <T>(current: T, action: SetStateAction<T>): T => {
  if (typeof action === 'function') {
    return (action as (value: T) => T)(current);
  }
  return action;
};

const resolveAgentComposerMutationKey = (
  sessionKey: string | null | undefined
): string | null => {
  if (!sessionKey) return null;

  const state = useWorkspaceStore.getState();
  const snapshot = state.agentComposerByKey[sessionKey];
  if (!snapshot || !sessionKey.endsWith(':draft')) return sessionKey;

  const migratedSessionId =
    snapshot.persistedSessionId ?? snapshot.localDraftSessionId;
  if (!migratedSessionId) return sessionKey;

  const routedKey = `${sessionKey.slice(0, -'draft'.length)}${migratedSessionId}`;
  return state.agentComposerByKey[routedKey] ? routedKey : sessionKey;
};

export const getAgentComposerSession = (
  sessionKey: string | null | undefined
): AgentComposerSessionSnapshot =>
  readAgentComposerSessionSnapshot(resolveAgentComposerMutationKey(sessionKey));

export const patchAgentComposerSessionByKey = (
  sessionKey: string | null | undefined,
  patch: Partial<AgentComposerSessionSnapshot>
): void => {
  const resolvedKey = resolveAgentComposerMutationKey(sessionKey);
  if (!resolvedKey) return;
  useWorkspaceStore.getState().patchAgentComposerSession(resolvedKey, patch);
};

export const updateAgentComposerSessionField = <
  K extends keyof AgentComposerSessionSnapshot,
>(
  sessionKey: string | null | undefined,
  field: K,
  action: SetStateAction<AgentComposerSessionSnapshot[K]>
): void => {
  const resolvedKey = resolveAgentComposerMutationKey(sessionKey);
  if (!resolvedKey) return;
  const current = getAgentComposerSession(sessionKey);
  patchAgentComposerSessionByKey(resolvedKey, {
    [field]: resolveSetStateAction(current[field], action),
  });
};

export const setAgentComposerSessionMessagesByKey = (
  sessionKey: string | null | undefined,
  action: SetStateAction<AgentChatUiMessage[]>
): void => {
  const resolvedKey = resolveAgentComposerMutationKey(sessionKey);
  if (!resolvedKey) return;
  useWorkspaceStore.getState().setAgentComposerMessages(resolvedKey, action);
};

export const setAgentComposerSessionReferenceImagesByKey = (
  sessionKey: string | null | undefined,
  action: SetStateAction<AgentReferenceImage[]>
): void => {
  const resolvedKey = resolveAgentComposerMutationKey(sessionKey);
  if (!resolvedKey) return;
  useWorkspaceStore
    .getState()
    .setAgentComposerReferenceImages(resolvedKey, action);
};

export const resetAgentComposerSessionTitleByKey = (
  sessionKey: string | null | undefined
): void => {
  patchAgentComposerSessionByKey(sessionKey, {
    sessionTitle: DEFAULT_AGENT_COMPOSER_SESSION_TITLE,
  });
};

type AgentComposerScalarField = Exclude<
  keyof AgentComposerSessionSnapshot,
  'messages' | 'referenceImages'
>;

export type AgentComposerSessionMutations = {
  get: () => AgentComposerSessionSnapshot;
  patch: (patch: Partial<AgentComposerSessionSnapshot>) => void;
  setMessages: (action: SetStateAction<AgentChatUiMessage[]>) => void;
  setReferenceImages: (action: SetStateAction<AgentReferenceImage[]>) => void;
  resetSessionTitle: () => void;
} & {
  [K in AgentComposerScalarField as `set${Capitalize<K>}`]: (
    action: SetStateAction<AgentComposerSessionSnapshot[K]>
  ) => void;
};

export const createAgentComposerSessionMutations = (
  sessionKey: string | null | undefined
): AgentComposerSessionMutations => {
  const get = (): AgentComposerSessionSnapshot =>
    getAgentComposerSession(sessionKey ?? null);

  const patch = (patchValue: Partial<AgentComposerSessionSnapshot>): void => {
    patchAgentComposerSessionByKey(sessionKey, patchValue);
  };

  const setField =
    <K extends keyof AgentComposerSessionSnapshot>(field: K) =>
    (action: SetStateAction<AgentComposerSessionSnapshot[K]>): void => {
      updateAgentComposerSessionField(sessionKey, field, action);
    };

  return {
    get,
    patch,
    setMessages: (action: SetStateAction<AgentChatUiMessage[]>): void =>
      setAgentComposerSessionMessagesByKey(sessionKey, action),
    setReferenceImages: (action: SetStateAction<AgentReferenceImage[]>): void =>
      setAgentComposerSessionReferenceImagesByKey(sessionKey, action),
    setInput: setField('input'),
    setAssetAttachments: setField('assetAttachments'),
    setAssetAttachmentError: setField('assetAttachmentError'),
    setActiveSkillId: setField('activeSkillId'),
    setDirectVideoSetup: setField('directVideoSetup'),
    setLoading: setField('loading'),
    setIsStreaming: setField('isStreaming'),
    setIsThinking: setField('isThinking'),
    setActivePlan: setField('activePlan'),
    setPlanning: setField('planning'),
    setApprovingPlanStepId: setField('approvingPlanStepId'),
    setConfirmingPlanStepId: setField('confirmingPlanStepId'),
    setRecoveringPlanStepId: setField('recoveringPlanStepId'),
    setActivePlanExecutions: setField('activePlanExecutions'),
    setSessionEngaged: setField('sessionEngaged'),
    setSessionLoading: setField('sessionLoading'),
    setPersistedSessionId: setField('persistedSessionId'),
    setLocalDraftSessionId: setField('localDraftSessionId'),
    setSuppressRouteSessionId: setField('suppressRouteSessionId'),
    setTitleLocked: setField('titleLocked'),
    setPendingFirstInput: setField('pendingFirstInput'),
    setIsHydrating: setField('isHydrating'),
    setHydrationGeneration: setField('hydrationGeneration'),
    setSkipNextGalleryRefresh: setField('skipNextGalleryRefresh'),
    setRemoteGalleryItems: setField('remoteGalleryItems'),
    setGalleryLoading: setField('galleryLoading'),
    setGalleryStageId: setField('galleryStageId'),
    setGalleryTab: setField('galleryTab'),
    setEditorProjectId: setField('editorProjectId'),
    setEditorOpening: setField('editorOpening'),
    setGalleryFocus: setField('galleryFocus'),
    setPendingLaunchToken: setField('pendingLaunchToken'),
    setSessionTitle: setField('sessionTitle'),
    resetSessionTitle: (): void =>
      resetAgentComposerSessionTitleByKey(sessionKey),
  };
};
